import React, { useCallback, Fragment, useState } from 'react';
import { List, Datagrid,
    Edit, Create, SimpleForm,
    TextField, ReferenceArrayField,
    EditButton, DeleteButton,
    TextInput, ReferenceArrayInput, SelectArrayInput,
    SingleFieldList, ChipField, BooleanInput,
    useQuery, Loading, Error,
    SaveButton, Toolbar,
    useCreate, useUpdate, useRedirect, useNotify,
    required, email, useTranslate,
    useMutation, Button, Confirm } from 'react-admin';
import SupervisorIcon from '@material-ui/icons/SupervisedUserCircle';
import School from '@material-ui/icons/School';

import { SectionTitle, Separator, HistoryAside,
    NameInput,
    validatePasswords } from './ui_components';
import { storeRole } from '../authProvider';

const SupervisorEditButton = ({ loginUser, ...props }) => {
  const editable = loginUser.is_owner || loginUser.id === props.record.id;
  return editable && <EditButton {...props} undoable="false" />
}

const SupervisorDeleteButton = props => {
  const translate = useTranslate();
  const confirmTitle = translate('resources.messages.delete_title', { name: `${props.record.name}` });
  return props.record.role !== 'owner' && <DeleteButton {...props} undoable={false} confirmTitle={confirmTitle} />
}

const TransferOwnerButton = ({ record }) => {
  const [open, setOpen] = useState(false);

  const handleClick = (event) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleDialogClose = () => setOpen(false);

  const translate = useTranslate();
  const notify = useNotify();
  const [transferRequest, { loading }] = useMutation(
    {
      type: 'create',
      resource: 'schools/transfer_owner',
      payload: { data: { new_owner_id: record.id } }
    },
    {
      onSuccess: ({data}) => {
        notify('ra.notification.transfer_owner_completed', 'info', {}, false);
        storeRole(data["role"]);
        setTimeout(() => window.location.reload(), 2000);
      },
      onFailure: () => {
        notify('ra.notification.bad_item', 'error');
      },
    }
  );

  const handleConfirm = () => {
    transferRequest();
    setOpen(false);
  };

  const confirmContent = translate('resources.messages.transfer_owner.content', { name: `${record.name}` });

  return record.role !== 'owner' && (
    <Fragment>
      <Button
        color="default"
        label={translate('resources.supervisors.button.transfer_owner')}
        onClick={handleClick}
        disabled={loading}
      >
        <School />
      </Button>
      <Confirm
        isOpen={open}
        title={translate('resources.messages.transfer_owner.title')}
        content={confirmContent}
        confirmColor="primary"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </Fragment>
  );
}

const SupervisorList = ({ permissions, ...props }) => {
  const { data, loading, error } = useQuery({ 
    type: 'getOne',
    resource: 'profiles',
    payload: { id: 0 }
  });

  if (loading) return <Loading />;
  if (error || !data) return <Error />;

  const isOwner = permissions && permissions.isOwner;
  const hasCreate = data.supervisor_creatable;
  return <List {...props} hasCreate={hasCreate} bulkActionButtons={false} exporter={false} >
    <Datagrid>
      <TextField source="last_name" />
      <TextField source="first_name" />
      <TextField source="email" />
      <TextField source="role" />
      <ReferenceArrayField source="lesson_group_ids" reference="lesson_groups" sortable={false}>
        <SingleFieldList>
            <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <SupervisorEditButton loginUser={data} />
      { isOwner && <SupervisorDeleteButton /> }
      { isOwner && <TransferOwnerButton /> }
    </Datagrid>
  </List>
};

const SupervisorEditSaveButton = props => {
  const { resource, basePath, record } = props;
  const [update, { loading }] = useUpdate(resource, record.id);
  const redirectTo = useRedirect();
  const notify = useNotify();

  const handleSave = useCallback(
    (values, redirect) => {
      update(
        {
          payload: { data: values },
        },
        {
          onSuccess: (params) => {
            notify('ra.notification.updated', 'info', { smart_count: 1 }, false);
            redirectTo(redirect, basePath);
          },
          onFailure: (error) => {
            console.log({ status: error.status, message: error.message, body: error.body });
            const message = error.message ? `resources.errors.${error.message}` : 'ra.notification.data_provider_error';
            notify(message, 'warning');
          },
        }
      );
    },
    [update, notify, redirectTo, basePath]
  );

  return <SaveButton {...props} onSave={handleSave} undoable="false" disabled={loading} />;
};

const SupervisorEditToolbar = props => (
  <Toolbar {...props}>
    <SupervisorEditSaveButton />
  </Toolbar>
);

const SupervisorEdit = props => {
  return (<Edit {...props} undoable="false" aside={<HistoryAside />}>
    <SimpleForm validate={validatePasswords} toolbar={<SupervisorEditToolbar />}>
      <SectionTitle label="resources.labels.profile" />
      <NameInput />
      <TextInput source="email" validate={[required(), email()]} style={{ width: '100%' }}/>
      <Separator />
      <SectionTitle label="resources.labels.management" />
      <ReferenceArrayInput source="lesson_group_ids" reference="lesson_groups" style={{ width: '100%' }}>
        <SelectArrayInput optionText="name" validate={[required()]} />
      </ReferenceArrayInput>
      <TextField source="role" />
      <Separator />
      <SectionTitle label="resources.labels.password" />
      <BooleanInput source="password_reset" />
      <TextField source="initial_password" />
    </SimpleForm>
  </Edit>);
};

const SupervisorCreateSaveButton = props => {
  const { resource, basePath } = props;
  const [create, { loading }] = useCreate(resource);
  const redirectTo = useRedirect();
  const notify = useNotify();

  const handleSave = useCallback(
    (values, redirect) => {
      create(
        {
          payload: { data: values },
        },
        {
          onSuccess: (params) => {
            notify('ra.notification.created', 'info', {}, false);
            redirectTo(redirect, basePath);
          },
          onFailure: (error) => {
            console.log(error);
            const message = error.message ? `resources.errors.${error.message}` : 'ra.notification.data_provider_error';
            notify(message, 'warning');
          },
        }
      );
    },
    [create, notify, redirectTo, basePath]
  );

  return <SaveButton {...props} onSave={handleSave} undoable="false" disabled={loading} />;
};

const SupervisorCreateToolbar = props => (
  <Toolbar {...props}>
    <SupervisorCreateSaveButton />
  </Toolbar>
);

const SupervisorCreate = props => (
  <Create {...props}>
    <SimpleForm redirect='list' toolbar={<SupervisorCreateToolbar />}>
      <SectionTitle label="resources.labels.profile" />
      <NameInput />
      <TextInput source="email" validate={[required(), email()]} style={{ width: '100%' }}/>
      <Separator />
      <SectionTitle label="resources.labels.management" />
      <ReferenceArrayInput source="lesson_group_ids" reference="lesson_groups" style={{ width: '100%' }}>
        <SelectArrayInput optionText="name" validate={[required()]} />
      </ReferenceArrayInput>
    </SimpleForm>
  </Create>
);

// profiles 取得して /supervisors/{id} に redirect
export const EditMe = () => {
  const redirectTo = useRedirect();
  const { data, loading, error } = useQuery({ 
    type: 'getOne',
    resource: 'profiles',
    payload: { id: 0 }
  });

  if (loading) return <Loading />;
  if (error || !data) return <Error />;

  redirectTo('/supervisors/' + data.id);
  return <Loading />
};

export default {
  list: SupervisorList,
  create: SupervisorCreate,
  edit: SupervisorEdit,
  icon: SupervisorIcon,
};
